import React, { FC, useState } from 'react';
import { InfoWindow, Marker } from '@react-google-maps/api';

import GoogleMapProvider from '../providers/GoogleMapProvider';
import { PartialAddress } from '../types/models/address_types';
import type { Procedure } from '../types/common';
import { VenueMapPreview } from './VenueMapPreview';

export interface GoogleMapCoord {
  lat: number;
  lng: number;
}

export interface MappableSpace {
  name: string;
  venue: {
    name: string;
    priceCategory: string;
  };
  standing?: number;
  seated?: number;
  images: {
    url: string;
  }[];
}

export interface MappableItem {
  key: string;
  name: string;
  position: GoogleMapCoord;
  images?: {
    url: string;
  }[];
  profileBanner?: string;
  spaces?: MappableSpace[];
  address?: PartialAddress;
  handleClick?: Procedure;
}

interface GoogleMapProps {
  mapStyle: { [key: string]: string };
  center?: GoogleMapCoord;
  mappableItems?: MappableItem[];
  handleLoad?: (map: google.maps.Map) => void;
  hoveredItemKey?: string;
}

export const GoogleMap: FC<GoogleMapProps> = ({
  mapStyle,
  center,
  mappableItems = [],
  handleLoad,
  hoveredItemKey,
}: GoogleMapProps) => {
  const [currentItem, setCurrentItem] = useState<MappableItem>();

  const handleMarkerClick = (mappableItem: MappableItem) => {
    const itemSpaces = mappableItem.spaces;
    if (itemSpaces && itemSpaces.length > 0) {
      setCurrentItem(mappableItem);
    } else if (mappableItem.handleClick) {
      mappableItem.handleClick();
    }
  };

  const handleCloseClick = () => {
    setCurrentItem(undefined);
  };

  if (!google) {
    return null;
  }

  return (
    <GoogleMapProvider
      containerStyle={mapStyle}
      center={center}
      handleLoad={handleLoad}
    >
      {mappableItems.map((mappableItem: MappableItem) => (
        <Marker
          key={mappableItem.key}
          title={mappableItem.name}
          icon={
            hoveredItemKey === mappableItem.key
              ? '/map-pin-hovered.svg'
              : '/map-pin.svg'
          }
          zIndex={hoveredItemKey === mappableItem.key ? 1000 : 0}
          position={mappableItem.position}
          onClick={() => handleMarkerClick(mappableItem)}
        />
      ))}
      {currentItem && (
        <InfoWindow
          onCloseClick={() => handleCloseClick()}
          position={{
            lat: currentItem?.position.lat ?? 0,
            lng: currentItem?.position.lng ?? 0,
          }}
          options={{
            maxWidth: 262,
            pixelOffset: new google.maps.Size(0, -36),
          }}
        >
          <VenueMapPreview
            venue={currentItem}
            handleClick={currentItem.handleClick}
          />
        </InfoWindow>
      )}
    </GoogleMapProvider>
  );
};
